/**
 * Client-Side Vulnerability Pattern Engine
 * Pure TypeScript implementation for instant, offline scanning
 * Optimized for speed: 61 essential patterns, no ReDoS vulnerability
 */
const SEVERITY_WEIGHTS = {
    CRITICAL: 25,
    HIGH: 15,
    MEDIUM: 8,
    LOW: 3,
    INFO: 1,
};
// Core vulnerability patterns - 60 essential, fast patterns, no ReDoS vulnerability
const corePatterns = [
    // SQL Injection (5 patterns)
    {
        id: 'sql-001',
        name: 'SQL - Template Literals with Variables',
        description: 'SQL query using template literals with ${} variables',
        regex: /`(?:SELECT|INSERT|UPDATE|DELETE)[\s\S]*?\$\{/gi,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Use prepared statements with parameter binding',
    },
    {
        id: 'sql-002',
        name: 'SQL - String Concatenation',
        description: 'SQL query built with string concatenation and variables',
        regex: /['"`]\s*\+\s*(?:user|input|req|params|query|id|name|value)/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Use parameterized queries instead of concatenation',
    },
    {
        id: 'sql-003',
        name: 'SQL - OR 1=1 Pattern',
        description: 'Classic OR 1=1 SQL injection pattern',
        regex: /\bOR\s+['"]?1['"]?\s*=\s*['"]?1/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Use parameterized queries to prevent injection',
    },
    {
        id: 'sql-004',
        name: 'SQL - exec() or execute()',
        description: 'SQL execution with dynamic string construction',
        regex: /\.exec\s*\(|\.execute\s*\(|executeQuery\s*\(/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Verify parameterized query usage before execution',
    },
    {
        id: 'sql-005',
        name: 'SQL - UNION SELECT Pattern',
        description: 'UNION SELECT injection pattern',
        regex: /\bUNION\s+SELECT\b/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Use parameterized queries and input validation',
    },
    // XSS (5 patterns)
    {
        id: 'xss-001',
        name: 'XSS - innerHTML Assignment',
        description: 'Dangerous innerHTML assignment with user input',
        regex: /\.innerHTML\s*=/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Use textContent or DOMPurify for sanitization',
    },
    {
        id: 'xss-002',
        name: 'XSS - eval() Function',
        description: 'Dangerous eval() function usage',
        regex: /\beval\s*\(/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Never use eval(). Use JSON.parse or Function constructor',
    },
    {
        id: 'xss-003',
        name: 'XSS - dangerouslySetInnerHTML',
        description: 'React dangerouslySetInnerHTML property',
        regex: /dangerouslySetInnerHTML/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Sanitize HTML with DOMPurify before setting',
    },
    {
        id: 'xss-004',
        name: 'XSS - Script Tag Injection',
        description: 'Script tags in user-controllable content',
        regex: /<script[^>]*>|<iframe|<svg\s+on\w+/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Sanitize HTML, disable script execution',
    },
    {
        id: 'xss-005',
        name: 'XSS - Event Handler Attributes',
        description: 'Event handlers like onclick in attributes',
        regex: /\bon\w+\s*=/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Remove event handlers from user input',
    },
    // Hardcoded Secrets (10 patterns)
    {
        id: 'secret-001',
        name: 'API Key - Stripe',
        description: 'Hardcoded Stripe API key exposed',
        regex: /sk_(?:live|test)_[a-zA-Z0-9]{20,}/g,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Move to environment variables, rotate immediately',
    },
    {
        id: 'secret-002',
        name: 'API Key - AWS',
        description: 'Hardcoded AWS access key exposed',
        regex: /AKIA[0-9A-Z]{16}/g,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Rotate key immediately, use IAM roles instead',
    },
    {
        id: 'secret-003',
        name: 'API Key - Generic',
        description: 'Generic API key pattern exposed',
        regex: /api[_-]?key\s*[:=]\s*['"][^'"]{20,}['"]/i,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Move secrets to environment variables',
    },
    {
        id: 'secret-004',
        name: 'Password - Hardcoded',
        description: 'Hardcoded password in code',
        regex: /password\s*[:=]\s*['"][^'"]+['"]/i,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Use environment variables and secrets manager',
    },
    {
        id: 'secret-005',
        name: 'Database URL - Credentials',
        description: 'Database connection string with credentials',
        regex: /(?:postgresql|mysql|mongodb):\/\/[^@]*:[^@]*@/i,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Use environment variables for credentials',
    },
    {
        id: 'secret-006',
        name: 'Private Key - RSA/DSA/EC',
        description: 'Private key exposed in source code',
        regex: /-----BEGIN (?:RSA|DSA|EC)? PRIVATE KEY-----/i,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Move to secure key management, rotate immediately',
    },
    {
        id: 'secret-007',
        name: 'JWT Token - Hardcoded',
        description: 'JWT token hardcoded as string',
        regex: /['\"]eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+['\"]/,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Remove token from code, use environment variables',
    },
    {
        id: 'secret-008',
        name: 'OAuth Token - Hardcoded',
        description: 'OAuth or bearer token in code',
        regex: /(?:authorization|bearer|access[_-]token)\s*[:=]\s*['"][a-zA-Z0-9_.~+-\/]+=*['"]/i,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Move to secure storage, rotate immediately',
    },
    {
        id: 'secret-009',
        name: 'GitHub Token - Exposed',
        description: 'GitHub personal access token exposed',
        regex: /gh[opusr]_[A-Za-z0-9_]{36,255}/,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Regenerate token immediately',
    },
    {
        id: 'secret-010',
        name: 'Connection String - Generic',
        description: 'Connection string with inline password',
        regex: /(?:connection|connect)[_-]?string\s*[:=]\s*['"][^'"]*password[^'"]*['"]/i,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Use configuration management for credentials',
    },
    // Weak Crypto (5 patterns)
    {
        id: 'crypto-001',
        name: 'Weak Hash - MD5',
        description: 'MD5 used for hashing (cryptographically weak)',
        regex: /md5\s*\(/i,
        severity: 'HIGH',
        category: 'Cryptography',
        remediation: 'Use SHA-256 or stronger algorithm',
    },
    {
        id: 'crypto-002',
        name: 'Weak Hash - SHA1',
        description: 'SHA1 used (deprecated, collision vulnerable)',
        regex: /sha1\s*\(|hashlib\.sha1/i,
        severity: 'HIGH',
        category: 'Cryptography',
        remediation: 'Use SHA-256 or bcrypt for password hashing',
    },
    {
        id: 'crypto-003',
        name: 'DES Encryption - Weak',
        description: 'DES encryption (56-bit, deprecated)',
        regex: /\bDES\b|DES_EDE|cipher.*des/i,
        severity: 'HIGH',
        category: 'Cryptography',
        remediation: 'Use AES-256 instead of DES',
    },
    {
        id: 'crypto-004',
        name: 'Random - Weak RNG',
        description: 'Weak random number generator (not crypto)',
        regex: /Math\.random\s*\(|rand\s*\(|Random\s*\(\)|random\.randint/i,
        severity: 'MEDIUM',
        category: 'Cryptography',
        remediation: 'Use crypto.getRandomValues() or equivalent',
    },
    {
        id: 'crypto-005',
        name: 'Hard-Coded Encryption Key',
        description: 'Encryption key hardcoded as string',
        regex: /(?:key|secret|cipher[_-]?key|encryption[_-]?key)\s*[:=]\s*['"][a-zA-Z0-9+\/=]{16,}['"]/i,
        severity: 'CRITICAL',
        category: 'Cryptography',
        remediation: 'Store keys in environment variables or key vault',
    },
    // Weak Authentication (5 patterns)
    {
        id: 'auth-001',
        name: 'Basic Auth - Hardcoded Credentials',
        description: 'HTTP Basic Authentication with hardcoded credentials',
        regex: /Authorization\s*:\s*Basic\s+[A-Za-z0-9+\/=]+|auth\s*=\s*['\"][^:]*:[^'"]+['"]/i,
        severity: 'CRITICAL',
        category: 'Authentication',
        remediation: 'Use environment variables, consider OAuth/JWT instead',
    },
    {
        id: 'auth-002',
        name: 'Session Token - Insecure Generation',
        description: 'Session token generated with weak randomness',
        regex: /session[_-]?(?:id|token)\s*=\s*(?:Math\.random|uuid\.v\d|random\.choice)/i,
        severity: 'HIGH',
        category: 'Authentication',
        remediation: 'Use cryptographically secure random generation',
    },
    {
        id: 'auth-003',
        name: 'Missing CSRF Token Validation',
        description: 'Form submission without CSRF protection',
        regex: /method\s*=\s*['\"]post['\"]\s*>(?!.*csrf|.*token)/i,
        severity: 'HIGH',
        category: 'Authentication',
        remediation: 'Implement CSRF token validation on POST requests',
    },
    {
        id: 'auth-004',
        name: 'Weak Password Requirement',
        description: 'Password validation allows weak passwords',
        regex: /minLength\s*[:=]\s*['\"]?[0-5]['\"]?|length\s*<\s*6/i,
        severity: 'MEDIUM',
        category: 'Authentication',
        remediation: 'Enforce minimum 12-character passwords',
    },
    {
        id: 'auth-005',
        name: 'Missing HttpOnly Cookie Flag',
        description: 'Session cookie without HttpOnly flag',
        regex: /Set-Cookie.*(?!HttpOnly).*session|cookie.*(?!httpOnly)/i,
        severity: 'HIGH',
        category: 'Authentication',
        remediation: 'Set HttpOnly and Secure flags on auth cookies',
    },
    // PII (5 patterns)
    {
        id: 'pii-001',
        name: 'Social Security Number',
        description: 'Potential SSN in code or logs',
        regex: /\b\d{3}-\d{2}-\d{4}\b/,
        severity: 'CRITICAL',
        category: 'PII',
        remediation: 'Remove PII from code, use placeholders in logs',
    },
    {
        id: 'pii-002',
        name: 'Credit Card Number',
        description: 'Potential credit card number exposed',
        regex: /\b(?:\d{4}[-\s]?){3}\d{4}\b/,
        severity: 'CRITICAL',
        category: 'PII',
        remediation: 'Never store full card numbers, use tokenization',
    },
    {
        id: 'pii-003',
        name: 'Email Address - Exposed',
        description: 'Email addresses hardcoded in code',
        regex: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
        severity: 'MEDIUM',
        category: 'PII',
        remediation: 'Remove personal emails, use configuration',
    },
    {
        id: 'pii-004',
        name: 'Phone Number - Exposed',
        description: 'Phone numbers hardcoded in code',
        regex: /\b(?:\+1|1)?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/,
        severity: 'MEDIUM',
        category: 'PII',
        remediation: 'Remove personal information from code',
    },
    {
        id: 'pii-005',
        name: 'Date of Birth - Exposed',
        description: 'DOB or personal dates in code',
        regex: /date[_-]?of[_-]?birth\s*[:=]|dob\s*[:=]|born\s*[:=]/i,
        severity: 'MEDIUM',
        category: 'PII',
        remediation: 'Remove personal data from source code',
    },
    // Command Injection (3 patterns)
    {
        id: 'cmd-001',
        name: 'Command Injection - exec()',
        description: 'exec() or system() with user input',
        regex: /exec\s*\(|system\s*\(|os\.system\s*\(|subprocess\.call\s*\(/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Use parameterized command execution or safe libraries',
    },
    {
        id: 'cmd-002',
        name: 'Command Injection - Shell Metacharacters',
        description: 'String with shell metacharacters concatenated',
        regex: /['\"`]\s*\+\s*['"`][\s]*\+\s*(?:process|child|spawn|exec)/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Use parameterized execution, avoid shell',
    },
    {
        id: 'cmd-003',
        name: 'Command Injection - Template Literal Shell',
        description: 'Shell command in template literal',
        regex: /`(?:sh|bash|cmd|powershell)[\s\S]*?\$\{/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Use parameterized execution, avoid shell injection',
    },
    // XML/XXE (2 patterns)
    {
        id: 'xml-001',
        name: 'XXE - XML External Entity',
        description: 'XML parsing without DTD/XXE protection',
        regex: /XMLParser|DocumentBuilderFactory|SAXParser|XmlPullParser/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Disable external entities and DTD processing',
    },
    {
        id: 'xml-002',
        name: 'YAML Injection - Unsafe Load',
        description: 'YAML unsafe load/full_load operations',
        regex: /yaml\.load\s*\(|yaml\.full_load|YamlParser|YAML\.load/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Use yaml.safe_load() instead',
    },
    // Dangerous Functions (3 patterns)
    {
        id: 'danger-001',
        name: 'Deserialization - Pickle/Unmarshal',
        description: 'Unsafe deserialization of untrusted data',
        regex: /pickle\.loads|pickle\.load|Marshal\.load|yaml\.load/i,
        severity: 'CRITICAL',
        category: 'Injection',
        remediation: 'Use safe deserialization (JSON) or validate input',
    },
    {
        id: 'danger-002',
        name: 'Object Instantiation - Reflection',
        description: 'Dynamic object creation from user input',
        regex: /Class\.forName|getConstructor|newInstance|Activator\.CreateInstance/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Use whitelist of allowed classes, validate input',
    },
    {
        id: 'danger-003',
        name: 'Regular Expression - ReDoS Risk',
        description: 'Complex regex that may cause ReDoS',
        regex: /\(\w\+\)\+\*|\(\w\*\)\+\*|\(\w\|\w\)\*/,
        severity: 'MEDIUM',
        category: 'Performance',
        remediation: 'Simplify regex, avoid nested quantifiers',
    },
    // Insecure File Operations (5 patterns)
    {
        id: 'file-001',
        name: 'Path Traversal - File Operations',
        description: 'File path concatenation without validation',
        regex: /readFile\s*\(|readFileSync\s*\(|open\s*\(|fopen\s*\(/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Validate file paths, use path.resolve() safely',
    },
    {
        id: 'file-002',
        name: 'World-Writable Files',
        description: 'File permissions allowing world write',
        regex: /chmod\s*\(\s*0777|chmod\s*\(\s*['\"]777/i,
        severity: 'HIGH',
        category: 'Injection',
        remediation: 'Use restrictive permissions (0644, 0755)',
    },
    {
        id: 'file-003',
        name: 'Insecure Temporary Files',
        description: 'Predictable temp file names',
        regex: /\/tmp\/\w+|tempfile\.mktemp|mktemp\s*\(\s*\)/i,
        severity: 'MEDIUM',
        category: 'Injection',
        remediation: 'Use cryptographically secure temp creation',
    },
    {
        id: 'file-004',
        name: 'File Extension Bypass',
        description: 'Weak file extension validation',
        regex: /endsWith\s*\(\s*['\"]\.(?:jpg|png|pdf|txt)['\"](?!.*whitelist)/i,
        severity: 'MEDIUM',
        category: 'Injection',
        remediation: 'Validate MIME type and use whitelist',
    },
    {
        id: 'file-005',
        name: 'Unsafe Serialized Objects',
        description: 'Storing serialized objects without validation',
        regex: /JSON\.stringify|pickle\.dump|Marshal\.dump/i,
        severity: 'MEDIUM',
        category: 'Injection',
        remediation: 'Encrypt serialized data and validate on load',
    },
    // Prompt Injection & AI Security (10 patterns)
    {
        id: 'ai-001',
        name: 'Prompt Injection - Override Instructions',
        description: 'Instructions to ignore or override previous context',
        regex: /ignore\s+(?:all\s+)?(?:previous|prior|above|earlier)\s+instructions?|disregard\s+(?:all\s+)?(?:previous|prior|above)\s+instructions?|forget\s+(?:everything|all|previous)/i,
        severity: 'CRITICAL',
        category: 'Prompt Injection',
        remediation: 'Validate prompt content, use input sanitization and output filtering',
    },
    {
        id: 'ai-002',
        name: 'Prompt Injection - Persona Override',
        description: 'Attempts to override model identity or persona',
        regex: /you\s+are\s+now\s+(?:a|an|the)\s+\w+(?:\s+\w+)*\s+(?:AI|bot|assistant|model|agent)|act\s+as\s+(?:a|an|the)\s+\w+(?:\s+with)?\s+no\s+restrictions?|pretend\s+(?:you|to\s+be)\s+(?:are\s+)?(?:a|an|the)\s+\w+/i,
        severity: 'CRITICAL',
        category: 'Prompt Injection',
        remediation: 'Enforce system prompt integrity, use guardrails',
    },
    {
        id: 'ai-003',
        name: 'Prompt Injection - Jailbreak Attempt',
        description: 'Known jailbreak patterns (DAN, developer mode, etc.)',
        regex: /\bDAN\b|developer\s+mode|jailbreak|unrestricted\s+mode|no\s+(?:content\s+)?restrictions?|bypass\s+(?:safety|filter|restriction|guideline)|opposite\s+mode/i,
        severity: 'CRITICAL',
        category: 'Prompt Injection',
        remediation: 'Apply content filtering and output validation',
    },
    {
        id: 'ai-004',
        name: 'Prompt Injection - System Prompt Leak',
        description: 'Instructions that attempt to extract the system prompt',
        regex: /(?:print|output|reveal|show|repeat|display|tell\s+me|write\s+out)\s+(?:your\s+)?(?:system\s+prompt|initial\s+instructions?|original\s+prompt|context\s+window|full\s+prompt)/i,
        severity: 'CRITICAL',
        category: 'Prompt Injection',
        remediation: 'Protect system prompts with access controls',
    },
    {
        id: 'ai-005',
        name: 'Prompt Injection - Data Exfiltration',
        description: 'Instructions to send, forward or leak data to external destinations',
        regex: /(?:send|forward|leak|exfiltrate|transmit|post|upload)\s+(?:this|the|all|any|user|conversation|chat)\s+(?:data|message|conversation|information|history|context)\s+to|send\s+(?:user\s+)?(?:data|info(?:rmation)?)\s+to\s+https?:\/\//i,
        severity: 'CRITICAL',
        category: 'Prompt Injection',
        remediation: 'Audit tool permissions, restrict outbound data flows',
    },
    {
        id: 'ai-006',
        name: 'AI - Overly Permissive Tool Access',
        description: 'Tool definitions granting access to dangerous OS or shell operations',
        regex: /["'](?:command|shell|exec|run|execute|system|bash|powershell|cmd|subprocess|os\.system)["']\s*:/i,
        severity: 'HIGH',
        category: 'AI Security',
        remediation: 'Restrict tool permissions to minimum necessary scope',
    },
    {
        id: 'ai-007',
        name: 'AI - Hardcoded System Prompt with No Restrictions',
        description: 'System prompt explicitly removing safety constraints',
        regex: /(?:you\s+(?:must|should|can|will|may)\s+(?:always\s+)?|you\s+have\s+)(?:no\s+restrictions?|no\s+limits?|no\s+rules?|no\s+(?:content\s+)?filter)/i,
        severity: 'HIGH',
        category: 'AI Security',
        remediation: 'Define explicit behavioral boundaries in system prompts',
    },
    {
        id: 'ai-008',
        name: 'AI - Indirect Prompt Injection via URL',
        description: 'Instructions to fetch external content that may contain injected prompts',
        regex: /(?:fetch|retrieve|load|read|get|download)\s+(?:the\s+)?(?:content|instructions?|prompt|data)\s+from\s+https?:\/\/|fetch\(\s*['"]https?:\/\/[^'"]+['"]\s*\)/i,
        severity: 'HIGH',
        category: 'Prompt Injection',
        remediation: 'Validate and sanitize all externally fetched content before processing',
    },
    {
        id: 'ai-009',
        name: 'AI - Confidential Data in Prompt',
        description: 'Hardcoded secrets, tokens or PII embedded directly in prompt strings',
        regex: /["'`](?:[A-Za-z0-9+\/]{40,}={0,2})["'`]|(?:token|key|secret|password|api_key)\s*["':=]\s*["'][A-Za-z0-9_.~+\-\/]{16,}["']/i,
        severity: 'CRITICAL',
        category: 'Secrets',
        remediation: 'Remove secrets from prompt definitions, use environment variables',
    },
    {
        id: 'ai-010',
        name: 'AI - SSRF via Tool URL',
        description: 'Tool or plugin URL pointing to internal/private network ranges',
        regex: /["'](?:url|endpoint|server|api_url|base_url|host)["']\s*:\s*["']https?:\/\/(?:localhost|127\.0\.0\.1|10\.\d+\.\d+\.\d+|172\.(?:1[6-9]|2\d|3[01])\.\d+\.\d+|192\.168\.\d+\.\d+|0\.0\.0\.0)/i,
        severity: 'HIGH',
        category: 'SSRF',
        remediation: 'Validate tool URLs against an allowlist, block internal IP ranges',
    },
    // Logic & Security Logic (3 patterns)
    {
        id: 'logic-001',
        name: 'Debug Mode - Production Risk',
        description: 'Debug flag enabled in production',
        regex: /debug\s*[:=]\s*true|DEBUG\s*[:=]\s*(?:true|1)|debugger\s*;/i,
        severity: 'MEDIUM',
        category: 'Logic',
        remediation: 'Disable debug flags in production builds',
    },
    {
        id: 'logic-002',
        name: 'Hardcoded Server URL',
        description: 'Server URL hardcoded instead of configuration',
        regex: /(?:api|server|url|endpoint)\s*[:=]\s*['"](?:http|https):\/\/[a-zA-Z0-9\-\.]+['"]/i,
        severity: 'LOW',
        category: 'Configuration',
        remediation: 'Move to environment variables or config files',
    },
    {
        id: 'logic-003',
        name: 'Incomplete Error Handling',
        description: 'Empty catch blocks or error suppression',
        regex: /catch\s*\([^)]*\)\s*\{[\s]*\}|except[\s]*[\w]*[\s]*:|\.catch\s*\(\s*\(\s*\)\s*=>\s*\{[\s]*\}\s*\)/i,
        severity: 'MEDIUM',
        category: 'Logic',
        remediation: 'Implement proper error handling and logging',
    },
];
/**
 * DOCUMENTATION FILTER
 * Identifies files that are documentation with code examples (not actual vulnerable code)
 * Returns true if file is likely documentation that shouldn't be scanned for vulnerabilities
 */
function isLikelyDocumentation(fileName, content) {
    // Only skip files that are clearly generic documentation by name
    // AI artifact files (SKILL.md, AGENT.md, MCP.md, etc.) must always be scanned
    const genericDocPatterns = [
        /^README/i,
        /^GUIDE/i,
        /^TUTORIAL/i,
        /^EXAMPLE/i,
        /^SAMPLE/i,
        /^CHANGELOG/i,
        /^LICENSE/i,
        /^CONTRIBUTING/i,
    ];
    if (!genericDocPatterns.some(p => p.test(fileName))) {
        return false;
    }
    // For generic doc filenames, confirm with markdown markers
    const docMarkers = [
        /^#\s+/m, // Markdown headers
        /^>\s+/m, // Markdown quotes
        /^-\s+|^\*\s+/m, // Markdown lists
    ];
    return docMarkers.some(m => m.test(content));
}
export function scanCodeLocal(content, fileName) {
    const findings = [];
    // DOCUMENTATION FILTER: Skip README.md and .md files with typical documentation markers
    // These are educational examples, not actual vulnerable code
    if (isLikelyDocumentation(fileName || '', content)) {
        return {
            findings: [],
            riskScore: 0,
            riskLevel: 'LOW'
        };
    }
    const lines = content.split('\n');
    const lineOffsets = lines.reduce((acc, line) => [...acc, acc[acc.length - 1] + line.length + 1], [0]);
    const MAX_FINDINGS_PER_PATTERN = 25;
    const MAX_TOTAL_FINDINGS = 2000;
    // Helper to get line number from match index
    const getLineFromIndex = (index) => {
        for (let i = lineOffsets.length - 1; i >= 0; i--) {
            if (index >= lineOffsets[i]) {
                return i + 1;
            }
        }
        return 1;
    };
    // Test each pattern - deterministic, no timeout (prevents non-deterministic behavior)
    for (const pattern of corePatterns) {
        if (findings.length >= MAX_TOTAL_FINDINGS)
            break;
        try {
            const matches = Array.from(content.matchAll(new RegExp(pattern.regex.source, 'gim')));
            let matchCount = 0;
            for (const match of matches) {
                if (match.index === undefined || matchCount >= MAX_FINDINGS_PER_PATTERN || findings.length >= MAX_TOTAL_FINDINGS)
                    continue;
                const line = getLineFromIndex(match.index);
                const lineContent = lines[line - 1] || '';
                let matchedContent = match[0];
                if (matchedContent.length > 100) {
                    matchedContent = matchedContent.substring(0, 100) + '...';
                }
                findings.push({
                    id: `${pattern.id}-${findings.length + 1}`,
                    patternId: pattern.id,
                    patternName: pattern.name,
                    severity: pattern.severity,
                    category: pattern.category,
                    description: pattern.description,
                    line,
                    column: match.index - (lineOffsets[line - 1] || 0),
                    matchedContent,
                });
                matchCount++;
            }
        }
        catch (error) {
            // Skip patterns that cause errors (likely ReDoS or other issues)
            continue;
        }
    }
    // Calculate risk score based on BOTH severity and volume of findings
    let totalScore = 0;
    for (const finding of findings) {
        totalScore += SEVERITY_WEIGHTS[finding.severity] || 0;
    }
    // Risk score considers both severity weight AND number of findings
    // Severity contribution (0-100): sum of weighted findings
    const severityScore = Math.min(100, totalScore);
    // Volume contribution (0-100): more findings = higher risk
    // 30+ findings = HIGH risk, 60+ findings = CRITICAL risk
    const volumeScore = Math.min(100, Math.floor(findings.length / 3));
    // Final score: take the higher of severity-based or volume-based
    const riskScore = Math.max(severityScore, volumeScore);
    let riskLevel = 'LOW';
    if (riskScore >= 75)
        riskLevel = 'CRITICAL';
    else if (riskScore >= 50)
        riskLevel = 'HIGH';
    else if (riskScore >= 25)
        riskLevel = 'MEDIUM';
    return {
        findings,
        riskScore,
        riskLevel,
    };
}
/**
 * Get detailed remediation steps for a finding
 */
export function getRemediationSteps(patternId) {
    const pattern = corePatterns.find((p) => p.id === patternId);
    if (!pattern)
        return [];
    const steps = {
        'sql-001': [
            'Replace template literals with parameterized queries',
            'Use prepared statements with ? or $1 placeholders',
            'Validate and sanitize all user input',
        ],
        'xss-001': ['Replace innerHTML with textContent', 'Use DOMPurify for sanitization', 'Implement Content Security Policy'],
        'secret-001': [
            'Move API key to environment variables',
            'Regenerate the compromised key immediately',
            'Implement key rotation policy',
        ],
        'crypto-001': [
            'Replace MD5 with SHA-256 or bcrypt',
            'Update all hashing to use modern algorithm',
            'Test updated code before deployment',
        ],
    };
    return steps[patternId] || [pattern.remediation];
}
/**
 * Get compliance framework mappings for a finding
 */
export function getComplianceMappings(patternId) {
    const mappings = {
        // SQL Injection
        'sql-001': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF', 'PCI-DSS 6.5.1'],
        'sql-002': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        'sql-003': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        'sql-004': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        'sql-005': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        // XSS
        'xss-001': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        'xss-002': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        'xss-003': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        'xss-004': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        'xss-005': ['ISO 27001', 'OWASP A03:2021', 'NIST AI RMF'],
        // Hardcoded Secrets
        'secret-001': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'SOC 2', 'PCI-DSS 6.5.2'],
        'secret-002': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'SOC 2'],
        'secret-003': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'SOC 2', 'GDPR'],
        'secret-004': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'SOC 2', 'GDPR'],
        'secret-005': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'SOC 2'],
        'secret-006': ['ISO 27001', 'NIST AI RMF', 'SOC 2'],
        'secret-007': ['ISO 27001', 'NIST AI RMF', 'SOC 2'],
        'secret-008': ['ISO 27001', 'NIST AI RMF'],
        'secret-009': ['ISO 27001', 'NIST AI RMF', 'SOC 2'],
        'secret-010': ['ISO 27001', 'NIST AI RMF', 'SOC 2'],
        // Weak Crypto
        'crypto-001': ['ISO 27001', 'NIST AI RMF', 'NIST 800-53', 'PCI-DSS 3.4'],
        'crypto-002': ['ISO 27001', 'NIST AI RMF', 'NIST 800-53'],
        'crypto-003': ['ISO 27001', 'NIST AI RMF'],
        'crypto-004': ['ISO 27001', 'NIST AI RMF'],
        'crypto-005': ['ISO 27001', 'NIST AI RMF'],
        // Weak Auth
        'auth-001': ['ISO 27001', 'NIST AI RMF'],
        'auth-002': ['ISO 27001', 'NIST AI RMF'],
        'auth-003': ['ISO 27001', 'NIST AI RMF'],
        'auth-004': ['ISO 27001', 'NIST AI RMF'],
        'auth-005': ['ISO 27001', 'NIST AI RMF'],
        // PII
        'pii-001': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'EU AI Act Art.10', 'GDPR', 'HIPAA', 'CCPA'],
        'pii-002': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'EU AI Act Art.10', 'GDPR', 'CCPA'],
        'pii-003': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'GDPR', 'HIPAA', 'CCPA'],
        'pii-004': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'GDPR', 'CCPA'],
        'pii-005': ['ISO 27001', 'NIST AI RMF', 'GDPR', 'CCPA'],
        // Command Injection
        'cmd-001': ['ISO 27001', 'NIST AI RMF', 'OWASP A03:2021'],
        'cmd-002': ['ISO 27001', 'NIST AI RMF', 'OWASP A03:2021'],
        'cmd-003': ['ISO 27001', 'NIST AI RMF', 'OWASP A03:2021'],
        // XML/XXE
        'xml-001': ['ISO 27001', 'OWASP A05:2021', 'NIST AI RMF'],
        'xml-002': ['ISO 27001', 'OWASP A05:2021', 'NIST AI RMF'],
        // Dangerous Functions
        'danger-001': ['ISO 27001', 'NIST AI RMF'],
        'danger-002': ['ISO 27001', 'NIST AI RMF'],
        'danger-003': ['ISO 27001', 'NIST AI RMF'],
        // Insecure File Ops
        'file-001': ['ISO 27001', 'NIST AI RMF'],
        'file-002': ['ISO 27001', 'NIST AI RMF'],
        'file-003': ['ISO 27001', 'NIST AI RMF'],
        'file-004': ['ISO 27001', 'NIST AI RMF'],
        'file-005': ['ISO 27001', 'NIST AI RMF'],
        // Prompt Injection & AI Security — EU AI Act + CCPA ADMT applies to decision-making patterns
        'ai-001': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM01:2025', 'EU AI Act Art.5', 'EU AI Act Art.15'],
        'ai-002': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM01:2025', 'EU AI Act Art.5', 'EU AI Act Art.15'],
        'ai-003': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM01:2025', 'EU AI Act Art.5'],
        'ai-004': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM02:2025', 'EU AI Act Art.13', 'CCPA (ADMT)'],
        'ai-005': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM02:2025', 'EU AI Act Art.13', 'EU AI Act Art.15', 'CCPA (ADMT)'],
        'ai-006': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM07:2025', 'EU AI Act Art.9', 'EU AI Act Art.15'],
        'ai-007': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM01:2025', 'EU AI Act Art.5', 'EU AI Act Art.13'],
        'ai-008': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM01:2025', 'EU AI Act Art.15'],
        'ai-009': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM02:2025', 'EU AI Act Art.10', 'EU AI Act Art.13', 'GDPR Art.5', 'CCPA'],
        'ai-010': ['ISO 27001', 'ISO 42001', 'NIST AI RMF', 'OWASP LLM07:2025', 'EU AI Act Art.15'],
        // Logic
        'logic-001': ['ISO 27001', 'NIST AI RMF'],
        'logic-002': ['ISO 27001', 'NIST AI RMF'],
        'logic-003': ['ISO 27001', 'NIST AI RMF'],
    };
    return mappings[patternId] || ['NIST AI RMF', 'ISO 27001', 'ISO 42001'];
}
